import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

/**
 * DEV-11294: in-place search for the marketing site. The search button and ⌘K
 * used to hard-navigate to /home?search=1, dropping the user onto the Discover
 * home screen where they had to click search again. This overlay keeps the
 * user wherever they are on the public site and searches the same guest-tier
 * catalogs the app search uses, served through the /api/lc edge cache (no
 * client auth). Picking a result deep-links straight to its /topic page.
 *
 * Re-work (Jonathan): mirror the app's SearchModal widget, not a new design —
 * same top-pinned card / mobile bottom sheet, "Popular Searches" empty state,
 * avatar-led rows with match highlighting, grouped Coins/Stocks/Topics
 * sections, Esc chip + clear button (apps/app search-modal.tsx is the
 * reference).
 */

type Kind = 'coin' | 'stock' | 'topic';

interface SearchItem {
  label: string;
  ticker?: string;
  kind: Kind;
  /** Path segment after /topic/ — coins/stocks as <name-slug>-<ticker>,
   *  matching the app's canonical URL shape (use-navigate.ts, DEV-10505). */
  slug: string;
  /** Catalog rank (market_cap_rank / topic_rank) — lower is better. */
  rank: number;
  /** Listing logo URL (coins/stocks) — last avatar fallback, like the app. */
  logo?: string;
}

// Mirror of apps/app/src/lib/topic-avatar.ts topicSlug (DEV-10534): camelCase
// splits before lowercasing so "OpenAI" → "open-ai" matches the canonical
// LunarCrush /topic slug.
function topicSlug(title: string): string {
  return title
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/[.,!?'"()&]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 0)
    .join('-');
}

function deburr(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

// Mirror of apps/app/src/lib/asset-identity.ts stripCorpSuffix (DEV-10694 /
// DEV-10697): drop corporate suffixes so a bare brand ("Apple", "NVIDIA") can
// match the equity's full legal listing name ("Apple Inc.", "NVIDIA Corp.").
const CORP_SUFFIX_RE = /\b(corp|corporation|inc|incorporated|ltd|limited|plc|holdings?|group|com)\b/g;
function stripCorpSuffix(name: string): string {
  return name
    .toLowerCase()
    .replace(/[.,]/g, ' ')
    .replace(CORP_SUFFIX_RE, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const CDN = 'https://cdn.lunarcrush.com';

// Mirror of apps/app/src/lib/curated-avatar-overrides.ts (DEV-11066): a handful
// of admin-curated logos live under a filename that is NOT derivable from the
// topic slug (`Donald Trump → donald-j.-trump.png`, `spacex → spcx.png`). The
// derived `/others/{slug}.png` cascade 404s to initials for these, so the row
// needs the curated URL prepended. Keys are lowercase/hyphenated `topicSlug`
// output. DEV-11352: without the `donald-trump` entry, "Donald Trump" falls to
// a letter chip on the landing even though the curated portrait exists.
const CURATED_TOPIC_LOGOS: Readonly<Record<string, string>> = {
  'donald-trump': `${CDN}/others/donald-j.-trump.png`,
  spacex: `${CDN}/others/spcx.png`,
  'e-toro': `${CDN}/others/etoro-exchange.png`,
  etoro: `${CDN}/others/etoro-exchange.png`,
  blofin: `${CDN}/others/blofin-exchange.png`,
  coinex: `${CDN}/others/coinex-exchange.png`,
};

/** Curated override URL for a topic slug, trying the slug and its compact
 *  (de-hyphenated) form — mirrors the app's curatedOverrideFor. */
function curatedOverrideFor(slug: string): string | null {
  if (CURATED_TOPIC_LOGOS[slug]) return CURATED_TOPIC_LOGOS[slug];
  const compact = slug.replace(/-/g, '');
  if (compact !== slug && CURATED_TOPIC_LOGOS[compact]) return CURATED_TOPIC_LOGOS[compact];
  return null;
}

/**
 * Avatar candidate cascade, mirroring the app's search rows
 * (coinAvatarCandidates / pureTopicCandidates in topic-avatar.ts):
 * stocks lead with /stocks/{ticker}.png, coins with cdn/{slug}.png, and pure
 * topics stay on the curated /others/ namespace — name-squatting memecoins
 * 200 on the crypto cdn/{slug}.png path (DEV-11319 r2).
 */
function avatarCandidates(item: SearchItem): string[] {
  const slug = topicSlug(item.label);
  const ascii = deburr(slug);
  const urls: string[] = [];
  if (item.kind === 'topic') {
    const firstWord = slug.split('-')[0];
    const curated = curatedOverrideFor(slug);
    if (curated) urls.push(curated);
    urls.push(`${CDN}/others/${slug}.png`);
    if (ascii !== slug) urls.push(`${CDN}/others/${ascii}.png`);
    if (firstWord !== slug) urls.push(`${CDN}/others/${firstWord}.png`);
  } else {
    if (item.kind === 'stock' && item.ticker) {
      urls.push(`${CDN}/stocks/${item.ticker.toLowerCase()}.png`);
    }
    urls.push(`${CDN}/${slug}.png`);
    if (ascii !== slug) urls.push(`${CDN}/${ascii}.png`);
    urls.push(`${CDN}/others/${slug}.png`);
    if (item.logo) urls.push(item.logo);
  }
  return Array.from(new Set(urls));
}

/** Walks the candidate list on error, bottoming out at a letter chip — the
 *  same contract as the app's TopicAvatarDeferred. Key it by slug so a row
 *  reuse resets the walk (key-remount, not setState-in-effect). */
const RowAvatar: React.FC<{ candidates: string[]; letter: string }> = ({ candidates, letter }) => {
  const [idx, setIdx] = useState(0);
  const src = candidates[idx];
  if (!src) return <span className="search-overlay-avatar-fallback">{letter}</span>;
  return (
    <img
      className="search-overlay-avatar-img"
      src={src}
      alt=""
      loading="lazy"
      onError={() => setIdx((i) => i + 1)}
    />
  );
};

/** First case-insensitive occurrence of the query, wrapped in <mark> — same
 *  highlight treatment as the app's renderHighlighted. */
const Highlighted: React.FC<{ text: string; q: string }> = ({ text, q }) => {
  const i = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <mark className="search-overlay-mark">{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
};

async function fetchList<T>(path: string): Promise<T[]> {
  try {
    const res = await fetch(path);
    if (!res.ok) return [];
    const json = (await res.json()) as { data?: T[] };
    return Array.isArray(json.data) ? json.data : [];
  } catch {
    return [];
  }
}

interface CatalogAsset {
  name?: string;
  symbol?: string;
  market_cap_rank?: number;
  logo?: string;
}

interface CatalogTopic {
  topic?: string;
  title?: string;
  topic_rank?: number;
}

// Top slice is plenty for a nav search — the full coin catalog is ~10k rows
// of long-tail the marketing visitor won't be typing.
const CATALOG_LIMIT = 1000;
const MAX_RESULTS = 12;
const UNRANKED = Number.MAX_SAFE_INTEGER;

// Same curated empty-state list as the app's search modal (POPULAR_SEARCHES
// in search-modal.tsx). DEV-11352: "Donald Trump" (the person/topic), not the
// bare "Trump" — "Trump" slugs to `trump`, which resolves to the Official Trump
// memecoin (wrong route) and has no curated `/others/trump.png` logo. "Donald
// Trump" slugs to `donald-trump`: resolves to the topic and paints its curated
// portrait via CURATED_TOPIC_LOGOS. Kept in sync with the app's search modal.
const POPULAR_SEARCHES = [
  'Bitcoin', 'Ethereum', 'Solana', 'NVIDIA', 'AI Agents',
  'Donald Trump', 'Apple', 'XRP', 'Dogecoin', 'Tesla',
];

let catalogPromise: Promise<SearchItem[]> | null = null;

function loadCatalog(): Promise<SearchItem[]> {
  if (!catalogPromise) {
    const p = Promise.all([
      fetchList<CatalogAsset>(`/api/lc/coins/list/v2?sort=market_cap_rank&limit=${CATALOG_LIMIT}`),
      // DEV-11350: the stocks feed's `market_cap_rank` is broken upstream, so a
      // rank-sorted top-N drops mega-caps (Tesla lands past rank 1000 and never
      // enters the catalog — its Popular Searches row then has no ticker). Sort
      // by `market_cap` instead — the same fix apps/app getStocks applies — and
      // rank stocks by list position so the whole feed carries real tickers.
      fetchList<CatalogAsset>(`/api/lc/stocks/list/v1?sort=market_cap&limit=${CATALOG_LIMIT}`),
      fetchList<CatalogTopic>('/api/lc/topics/list/v1'),
    ]).then(([coins, stocks, topics]) => {
      const items: SearchItem[] = [];
      const assetNames = new Set<string>();
      const addAssets = (list: CatalogAsset[], kind: Kind) => {
        list.forEach((a, i) => {
          if (!a?.name || !a?.symbol) return;
          assetNames.add(a.name.toLowerCase());
          items.push({
            label: a.name,
            ticker: a.symbol,
            kind,
            slug: `${topicSlug(a.name)}-${a.symbol.toLowerCase()}`,
            // Stocks are fetched market_cap-sorted (rank field unreliable), so
            // their list position IS the rank; coins keep their real rank.
            rank: kind === 'stock' ? i : a.market_cap_rank ?? UNRANKED,
            logo: a.logo,
          });
        });
      };
      addAssets(coins, 'coin');
      addAssets(stocks, 'stock');
      for (const t of topics) {
        if (!t?.topic) continue;
        const label = t.title || t.topic;
        // The asset detail page wins over its namesake topic (same rule the
        // app search applies) — skip topics that shadow a listed coin/stock.
        if (assetNames.has(label.toLowerCase())) continue;
        items.push({ label, kind: 'topic', slug: topicSlug(t.topic), rank: t.topic_rank ?? UNRANKED });
      }
      // Don't pin a fully-failed load — the next open retries.
      if (items.length === 0 && catalogPromise === p) catalogPromise = null;
      return items;
    });
    catalogPromise = p;
  }
  return catalogPromise;
}

function searchCatalog(items: SearchItem[], query: string): SearchItem[] {
  const q = query.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!q) return [];
  const scored: { item: SearchItem; score: number }[] = [];
  for (const item of items) {
    const name = item.label.toLowerCase();
    const tick = item.ticker?.toLowerCase();
    let score: number;
    if (tick === q || name === q) score = 0;
    else if (name.startsWith(q) || (tick && tick.startsWith(q))) score = 1;
    else if (name.includes(q)) score = 2;
    else continue;
    scored.push({ item, score });
  }
  scored.sort((a, b) => a.score - b.score || a.item.rank - b.item.rank);
  return scored.slice(0, MAX_RESULTS).map((s) => s.item);
}

const OPEN_EVENT = 'lc:search-overlay-open';

/** Open the marketing-site search overlay (mounted once in Layout). */
export function openSearchOverlay(): void {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export const SearchOverlay: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [catalog, setCatalog] = useState<SearchItem[] | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setActiveIndex(-1);
  }, []);

  // Open on the nav-button event and on ⌘K / Ctrl+K anywhere on the site.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener(OPEN_EVENT, onOpen);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  // Fetch the catalog once, on first open.
  useEffect(() => {
    if (!open || catalog) return;
    let stale = false;
    loadCatalog().then((items) => {
      if (!stale) setCatalog(items);
    });
    return () => {
      stale = true;
    };
  }, [open, catalog]);

  // While open: lock body scroll, focus the input, close on Escape.
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const trimmed = query.trim();
  const results = useMemo(() => (catalog ? searchCatalog(catalog, query) : []), [catalog, query]);

  // Empty-state rows: the app's curated Popular Searches, resolved against
  // the catalog so known assets carry their ticker / type / avatar.
  const popularItems = useMemo<SearchItem[]>(
    () =>
      POPULAR_SEARCHES.map((name) => {
        const nameLc = name.toLowerCase();
        const found = catalog?.find((it) => it.label.toLowerCase() === nameLc);
        if (found) return found;
        // DEV-11350: stocks list their full legal name ("Apple Inc.", "NVIDIA
        // Corp."), so a bare-brand popular entry ("Apple"/"NVIDIA"/"Tesla")
        // never exact-matches and used to fall through to a tickerless topic
        // row. Fall back to a corp-suffix-stripped match against stocks so the
        // row resolves to the equity and carries its ticker, like coins do.
        const stock = catalog?.find(
          (it) => it.kind === 'stock' && stripCorpSuffix(it.label) === nameLc,
        );
        // Keep the curated brand label ("Apple"), but adopt the stock's ticker,
        // kind, slug and logo so the row routes to the equity like the app does.
        if (stock) return { ...stock, label: name };
        return { label: name, kind: 'topic', slug: topicSlug(name), rank: UNRANKED };
      }),
    [catalog],
  );

  // The keyboard walks one flat list: popular rows when idle, results when
  // typing. Rendering re-groups results by type (Coins/Stocks/Topics) with
  // the app's section headers, preserving flat indexes for selection.
  const rows = trimmed ? results : popularItems;
  const groups = useMemo(() => {
    const order: Kind[] = [];
    const byKind = new Map<Kind, { item: SearchItem; idx: number }[]>();
    results.forEach((item, idx) => {
      if (!byKind.has(item.kind)) {
        byKind.set(item.kind, []);
        order.push(item.kind);
      }
      byKind.get(item.kind)!.push({ item, idx });
    });
    return order.map((kind) => ({ kind, rows: byKind.get(kind)! }));
  }, [results]);

  const go = useCallback(
    (item?: SearchItem) => {
      const target = item ?? rows[activeIndex] ?? (trimmed ? rows[0] : undefined);
      // Unknown terms still get a topic page — the app renders its
      // "starting to collect" placeholder, same as the in-app search.
      const slug = target ? target.slug : topicSlug(trimmed);
      if (!slug) return;
      window.location.assign(`/topic/${encodeURIComponent(slug)}`);
    },
    [rows, activeIndex, trimmed],
  );

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, rows.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && (trimmed || activeIndex >= 0)) {
      e.preventDefault();
      go();
    }
  };

  // One row shape for popular + grouped results — avatar, name (highlighted),
  // ticker beside it, type chip only where the section header doesn't
  // already name the type (app parity: renderResultRow's showTypeChip).
  const renderRow = (item: SearchItem, i: number, showTypeChip: boolean, hl: string) => (
    <button
      key={`${item.kind}:${item.slug}`}
      className={i === activeIndex ? 'search-overlay-item active' : 'search-overlay-item'}
      onMouseMove={() => {
        if (activeIndex !== i) setActiveIndex(i);
      }}
      onClick={() => go(item)}
      role="option"
      aria-selected={i === activeIndex}
    >
      <span className="search-overlay-avatar">
        <RowAvatar
          key={item.slug}
          candidates={avatarCandidates(item)}
          letter={item.label.charAt(0).toUpperCase()}
        />
      </span>
      <span className="search-overlay-label">
        <span className="search-overlay-name">
          <Highlighted text={item.label} q={hl} />
        </span>
        {item.ticker && (
          <span className="search-overlay-ticker">
            <Highlighted text={item.ticker} q={hl.replace(/ /g, '')} />
          </span>
        )}
      </span>
      {showTypeChip && <span className="search-overlay-kind">{t(`search.${item.kind}`)}</span>}
    </button>
  );

  const sectionHeader = (label: string) => (
    <div className="search-overlay-section">
      <span className="search-overlay-section-label">{label}</span>
      <span className="search-overlay-section-rule" aria-hidden="true" />
    </div>
  );

  if (!open) return null;

  return createPortal(
    <div className="search-overlay" onClick={close} role="dialog" aria-modal="true" aria-label={t('common.search')}>
      <div className="search-overlay-panel" onClick={(e) => e.stopPropagation()}>
        <div className="search-overlay-input-row">
          <svg width="18" height="18" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true">
            <path d="M229.66,218.34l-50.07-50.06a88.11,88.11,0,1,0-11.31,11.31l50.06,50.07a8,8,0,0,0,11.32-11.32ZM40,112a72,72,0,1,1,72,72A72.08,72.08,0,0,1,40,112Z" />
          </svg>
          <input
            ref={inputRef}
            className="search-overlay-input"
            type="search"
            placeholder={t('search.placeholder')}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(-1);
            }}
            onKeyDown={onInputKeyDown}
            role="combobox"
            aria-expanded={rows.length > 0}
            aria-autocomplete="list"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            aria-label={t('common.search')}
          />
          {query && (
            <button
              className="search-overlay-clear"
              onClick={() => {
                setQuery('');
                setActiveIndex(-1);
                inputRef.current?.focus();
              }}
              aria-label={t('common.search')}
            >
              <svg width="14" height="14" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true">
                <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z" />
              </svg>
            </button>
          )}
          <kbd className="search-overlay-esc">Esc</kbd>
          <button className="search-overlay-close" onClick={close} aria-label={t('mobile.close')}>
            <svg width="20" height="20" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true">
              <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z" />
            </svg>
          </button>
        </div>
        <div className="search-overlay-results" role="listbox">
          {!trimmed ? (
            <>
              <p className="search-overlay-section-label search-overlay-popular-header">
                {t('search.popularSearches')}
              </p>
              {popularItems.map((item, i) => renderRow(item, i, true, ''))}
            </>
          ) : results.length > 0 ? (
            groups.map((g) => (
              <div key={g.kind}>
                {sectionHeader(t(`search.${g.kind}s`))}
                {g.rows.map(({ item, idx }) => renderRow(item, idx, false, trimmed))}
              </div>
            ))
          ) : catalog ? (
            <>
              <p className="search-overlay-empty">{t('search.noMatches')}</p>
              <button className="search-overlay-item search-overlay-search-for" onClick={() => go()}>
                <svg width="16" height="16" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true">
                  <path d="M229.66,218.34l-50.07-50.06a88.11,88.11,0,1,0-11.31,11.31l50.06,50.07a8,8,0,0,0,11.32-11.32ZM40,112a72,72,0,1,1,72,72A72.08,72.08,0,0,1,40,112Z" />
                </svg>
                <span className="search-overlay-name">
                  {t('search.searchFor')} <strong>&quot;{trimmed}&quot;</strong>
                </span>
              </button>
            </>
          ) : (
            <p className="search-overlay-empty">{t('search.loading')}</p>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};
